import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import { getNewsById, getRelatedNews } from "@/mocks/news";
import { getCategoryByType } from "@/mocks/categories";
import Breadcrumb from "@/components/feature/Breadcrumb";
import NewsCard from "@/components/feature/NewsCard";

const categoryLabel: Record<string, string> = {
  news: "演艺新闻",
  activity: "活动动态",
  life: "生活动态",
};

export default function NewsDetailPage() {
  const { id = "" } = useParams();
  const item = getNewsById(id);
  const [toast, setToast] = useState("");

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(""), 2200);
    return () => clearTimeout(timer);
  }, [toast]);

  useSEO({
    title: item ? `${item.title} - 小泽玛利亚资讯详情` : "资讯未找到 - 小泽玛利亚资讯",
    description: item ? item.summary : "该资讯暂时无法查看。",
    keywords: item ? `小泽玛利亚,${item.tags.join(",")}` : "小泽玛利亚,资讯",
  });

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setToast("链接已复制，快去分享吧");
    } catch {
      setToast("链接复制失败，请手动复制地址栏链接");
    }
  };

  const shareTo = (platform: string) => {
    const url = encodeURIComponent(window.location.href);
    const text = encodeURIComponent(item ? item.title : "");
    if (platform === "weibo") {
      window.open(`https://hutu-order.cn/share/share.php?url=${url}&title=${text}`, "_blank");
      return;
    }
    if (platform === "qq") {
      window.open(`https://connect.qq.com/widget/shareqq/index.html?url=${url}&title=${text}`, "_blank");
      return;
    }
    if (platform === "wechat") {
      setToast("请复制链接后在微信中打开分享");
    }
  };

  if (!item) {
    return (
      <div className="mx-auto max-w-content px-4 md:px-6 py-20 text-center">
        <i className="ri-file-unknow-line text-5xl text-primary-400"></i>
        <h1 className="font-heading text-2xl font-semibold text-foreground-950 mt-4">资讯不存在</h1>
        <p className="text-sm text-foreground-600 mt-2">这条资讯可能已被移除或链接有误。</p>
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 mt-6 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-home-line"></i>
          返回首页
        </Link>
      </div>
    );
  }

  const related = getRelatedNews(item, 3);
  const cat = getCategoryByType(item.category);

  return (
    <div className="animate-fade-up mx-auto max-w-content px-4 md:px-6 py-6 md:py-10">
      <Breadcrumb
        items={[
          { label: cat?.shortName ?? "资讯", to: `/category/${item.category}` },
          { label: "详情" },
        ]}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10 mt-5">
        <article className="lg:col-span-2">
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <Link
              to={`/category/${item.category}`}
              className="text-xs px-2.5 py-1 rounded-full bg-primary-100 text-primary-800 hover:bg-primary-200 transition-colors whitespace-nowrap cursor-pointer"
            >
              {categoryLabel[item.category] ?? "资讯"}
            </Link>
            <span className="text-xs text-foreground-500">{item.subCategory}</span>
          </div>

          <h1 className="font-heading text-2xl md:text-4xl font-semibold text-foreground-950 leading-snug mb-4">
            {item.title}
          </h1>

          <div className="flex items-center gap-4 flex-wrap text-sm text-foreground-500 pb-5 border-b border-background-200">
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line"></i>
              {item.date}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-newspaper-line"></i>
              来源：{item.source}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line"></i>
              {item.author}
            </span>
          </div>

          <div className="w-full aspect-[16/9] rounded-lg overflow-hidden bg-background-100 my-6">
            <img
              src={item.cover}
              alt={item.title}
              title={`${item.title} - 小泽玛利亚资讯`}
              className="w-full h-full object-top object-cover"
            />
          </div>

          <div className="flex flex-col gap-4">
            {item.content.map((p, idx) => (
              <p key={idx} className="text-sm md:text-base text-foreground-700 leading-loose">
                {p}
              </p>
            ))}
          </div>

          <div className="flex flex-wrap gap-2 mt-6">
            {item.tags.map((t) => (
              <span key={t} className="text-xs px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900">
                #{t}
              </span>
            ))}
          </div>

          <div className="mt-8 pt-6 border-t border-background-200">
            <h3 className="text-sm font-semibold text-foreground-950 mb-3 flex items-center gap-2">
              <i className="ri-share-line text-primary-600"></i>
              分享这条资讯
            </h3>
            <div className="flex flex-wrap gap-2.5">
              <button
                type="button"
                onClick={() => shareTo("weibo")}
                className="flex items-center gap-2 px-4 py-2 rounded-md border border-background-200 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-weibo-line text-accent-600"></i>
                微博
              </button>
              <button
                type="button"
                onClick={() => shareTo("wechat")}
                className="flex items-center gap-2 px-4 py-2 rounded-md border border-background-200 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-wechat-line text-primary-500"></i>
                微信
              </button>
              <button
                type="button"
                onClick={() => shareTo("qq")}
                className="flex items-center gap-2 px-4 py-2 rounded-md border border-background-200 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-qq-line text-secondary-600"></i>
                QQ
              </button>
              <button
                type="button"
                onClick={copyLink}
                className="flex items-center gap-2 px-4 py-2 rounded-md border border-background-200 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-link"></i>
                复制链接
              </button>
            </div>
          </div>
        </article>

        <aside className="lg:col-span-1">
          <div className="sticky top-24">
            <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-4 flex items-center gap-2">
              <i className="ri-magic-line text-primary-600"></i>
              相关推荐
            </h3>
            <div className="flex flex-col gap-4">
              {related.map((r) => (
                <NewsCard key={r.id} item={r} layout="horizontal" />
              ))}
            </div>
            <Link
              to={`/category/${item.category}`}
              className="inline-flex items-center gap-1.5 mt-5 text-sm text-primary-700 hover:text-primary-800 whitespace-nowrap cursor-pointer"
            >
              查看更多{categoryLabel[item.category]}
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </aside>
      </div>

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] bg-foreground-950 text-background-50 text-sm px-5 py-3 rounded-lg animate-fade-up">
          {toast}
        </div>
      )}
    </div>
  );
}